// A person's profile photo (staff.addPhoto): what the office may pick, what the profile shows while
// it waits for its check, and each way an upload fails, in words.

/** The photo types the server takes (media CONTENT_TYPES.photo) and its size limit. */
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
export const PHOTO_MAX_BYTES = 25_000_000;

/** Why a picked file can't be the photo, before anything is sent; null when it can. */
export function photoProblem(contentType: string, bytes: number): string | null {
  if (!PHOTO_TYPES.includes(contentType))
    return 'That isn’t a photo the profile can take: use a JPEG, PNG, HEIC or WebP.';
  if (bytes === 0) return 'That file is empty: pick the photo again.';
  if (bytes > PHOTO_MAX_BYTES)
    return `That photo is too big (${(bytes / 1_000_000).toFixed(1)} MB): the most is 25 MB.`;
  return null;
}

export type PhotoState = 'none' | 'checking' | 'shown';

/** A photo shows only once the bucket holds it whole (photoUrl); recorded but not checked yet waits. */
export const photoState = (s: { photoFileId: string | null; photoUrl: string | null }): PhotoState =>
  s.photoUrl ? 'shown' : s.photoFileId ? 'checking' : 'none';

export const PHOTO_WORDS: Record<PhotoState, string> = {
  none: 'No photo yet.',
  checking: 'A new photo is on their profile: it shows once it’s checked.',
  shown: '',
};

/** The upload to the file bucket: refused (by its status), or never reached. */
export function putFailText(status: number | null): string {
  if (status === null) return 'The photo didn’t reach the file store: check the connection, then try again.';
  if (status === 413) return 'The file store refused the photo as too big: pick a smaller one.';
  if (status === 403) return 'The file store refused the photo: its upload link may have run out. Try again.';
  return `The file store refused the photo (${status}). Try again.`;
}

/** files.confirmUpload's refusals (its reason): not there yet, or not the photo that was picked. */
export function confirmFailText(reason: string | undefined): string {
  if (reason === 'missing') return 'The photo hasn’t arrived in the file store yet. Try again.';
  if (reason === 'mismatch') return 'What arrived isn’t the photo you picked. Try again.';
  return 'The photo couldn’t be checked. Try again.';
}
